import React, { useEffect, useState } from 'react';
import { alpacaService, CalibrationBucket, CalibrationSummary } from '../../services/AlpacaService';
import styles from './Calibration.module.css';

const PERSONAL_MIN = 10; // threshold below which we show community baseline

const Calibration: React.FC = () => {
  const [data, setData] = useState<CalibrationSummary | null>(null);
  const [community, setCommunity] = useState<CalibrationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [scoring, setScoring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);

  const load = async (d: number) => {
    setLoading(true);
    setError(null);
    try {
      // Always fetch personal summary. Community is fetched in parallel so
      // it's available if we need to render the baseline fallback.
      const [personal, comm] = await Promise.all([
        alpacaService.getCalibrationSummary(d),
        alpacaService.getCalibrationCommunity(d).catch(() => null),
      ]);
      setData(personal);
      setCommunity(comm);
    } catch (e: any) {
      setError(e?.message || 'Failed to load calibration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(days);
  }, [days]);

  const runScore = async () => {
    setScoring(true);
    try {
      const result = await alpacaService.scoreCalibration();
      // Reload once scorer finishes
      await load(days);
      // eslint-disable-next-line no-alert
      alert(`Scored ${result.scored} decisions · skipped ${result.skipped} · errors ${result.errors}`);
    } catch (e: any) {
      setError(e?.message || 'Score run failed');
    } finally {
      setScoring(false);
    }
  };

  const renderBucketTable = (title: string, buckets: Record<string, CalibrationBucket>, keyOrder?: string[]) => {
    const keys = keyOrder ? keyOrder.filter((k) => buckets[k]) : Object.keys(buckets);
    if (keys.length === 0) return null;
    return (
      <div className={styles.section}>
        <h3>{title}</h3>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Bucket</th>
              <th>N</th>
              <th>Wins</th>
              <th>Losses</th>
              <th>Win Rate</th>
              <th>Avg +1H</th>
              <th>Avg +4H</th>
              <th>Calibration</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => {
              const b = buckets[k];
              // For confidence buckets, highlight miscalibration
              let calibNote = '';
              const confMidpoint: Record<string, number> = {
                '60-70': 65,
                '70-80': 75,
                '80-90': 85,
                '90+': 95,
              };
              if (confMidpoint[k] != null && b.winRate != null && b.count >= 5) {
                const delta = confMidpoint[k] - b.winRate;
                if (delta >= 15) calibNote = `⚠ overconfident by ~${delta.toFixed(0)}pts`;
                else if (delta <= -10) calibNote = '✓ well-calibrated';
              }
              return (
                <tr key={k}>
                  <td className={styles.bucketKey}>{k}</td>
                  <td>{b.count}</td>
                  <td className={styles.pos}>{b.wins ?? '—'}</td>
                  <td className={styles.neg}>{b.losses ?? '—'}</td>
                  <td
                    className={
                      b.winRate != null && b.winRate >= 55
                        ? styles.pos
                        : b.winRate != null && b.winRate < 45
                          ? styles.neg
                          : ''
                    }
                  >
                    {b.winRate != null ? `${b.winRate}%` : '—'}
                  </td>
                  <td>{b.avgPnl1h != null ? `${b.avgPnl1h >= 0 ? '+' : ''}${b.avgPnl1h}%` : '—'}</td>
                  <td>{b.avgPnl4h != null ? `${b.avgPnl4h >= 0 ? '+' : ''}${b.avgPnl4h}%` : '—'}</td>
                  <td className={styles.calibNote}>{calibNote}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>AI Calibration</h1>
          <p className={styles.subtitle}>
            Empirical win-rate of Claude's past Screener + QuickTrade calls, scored against actual price action. Fed
            back into the prompt so the model can self-correct.
          </p>
        </div>
        <div className={styles.controls}>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button onClick={runScore} disabled={scoring} className={styles.scoreBtn}>
            {scoring ? 'Scoring…' : 'Run Scorer'}
          </button>
        </div>
      </div>

      {loading && <div className={styles.loading}>Loading…</div>}
      {error && <div className={styles.error}>{error}</div>}

      {data &&
        !loading &&
        (() => {
          // When personal history is thin, fall back to community baseline so
          // new users still see empirical data. We flag the fallback clearly.
          const usingCommunity = data.totalScored < PERSONAL_MIN && community && community.totalScored >= PERSONAL_MIN;
          const displayed = usingCommunity ? community! : data;
          return (
            <>
              {usingCommunity && (
                <div className={styles.communityNotice}>
                  <strong>Community baseline shown</strong> — you have {data.totalScored} personally scored
                  {data.totalScored === 1 ? ' call' : ' calls'} so far (threshold: {PERSONAL_MIN}). Aggregate win-rate
                  across all SignalDeck users is displayed below as a starting reference. Your own stats will replace
                  this once you have {PERSONAL_MIN - data.totalScored} more scored decisions.
                </div>
              )}
              <div className={styles.overview}>
                <div className={styles.stat}>
                  <div className={styles.statLabel}>
                    {usingCommunity ? 'Community' : 'Your'} Scored Decisions ({displayed.days}d)
                  </div>
                  <div className={styles.statValue}>{displayed.totalScored}</div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.statLabel}>Overall Win Rate</div>
                  <div className={styles.statValue}>
                    {displayed.buckets.overall.winRate != null ? `${displayed.buckets.overall.winRate}%` : '—'}
                  </div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.statLabel}>Wins / Losses</div>
                  <div className={styles.statValue}>
                    {displayed.buckets.overall.wins ?? 0} / {displayed.buckets.overall.losses ?? 0}
                  </div>
                </div>
              </div>

              {data.totalScored === 0 && !usingCommunity && (
                <div className={styles.empty}>
                  No scored decisions yet. Run Screener / QuickTrade, wait &gt;1 hour for price action to develop, then
                  click "Run Scorer" (or it'll be picked up on the next EventBridge tick once scheduled).
                </div>
              )}

              {renderBucketTable('By Confidence Bucket', displayed.buckets.byConfidence, [
                '<50',
                '50-60',
                '60-70',
                '70-80',
                '80-90',
                '90+',
              ])}
              {renderBucketTable('By Session Phase', displayed.buckets.bySessionPhase, [
                'OPEN_RANGE',
                'MID_MORNING',
                'LUNCH',
                'AFTERNOON',
                'POWER_HOUR',
                'PREMARKET',
                'AFTER_HOURS',
              ])}
              {renderBucketTable('By Market Regime', displayed.buckets.byRegime)}
              {renderBucketTable('By Short Interest Bucket', displayed.buckets.byShortInterestBucket, [
                'LOW',
                'NORMAL',
                'ELEVATED',
                'HIGH',
              ])}
              {renderBucketTable('By Source', displayed.buckets.bySource)}
              <div className={styles.footer}>
                Generated {new Date(displayed.generatedAt).toLocaleString()}
                {usingCommunity ? ' · community aggregate' : ' · personal history'}
              </div>
            </>
          );
        })()}
    </div>
  );
};

export default Calibration;
